import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { PageLayout, PageHeader, Card, Button } from '../components/ui'

interface Preferences {
  email_reminders_enabled: boolean
  reminder_frequency: string
  survey_reminders: boolean
  organization_updates: boolean
  quiet_hours_enabled: boolean
  quiet_hours_start: string | null
  quiet_hours_end: string | null
  timezone: string
  muted_survey_ids: string[]
  unsubscribe_token: string | null
}

const FREQUENCY_OPTIONS = [
  { value: 'default', labelId: 'notif.freqDefault' },
  { value: 'daily_digest', labelId: 'notif.freqDailyDigest' },
  { value: 'weekly_digest', labelId: 'notif.freqWeeklyDigest' },
  { value: 'never', labelId: 'notif.freqNever' },
]

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Montreal',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
]

const NotificationSettingsPage: React.FC = () => {
  const intl = useIntl()
  useAuth()
  const [prefs, setPrefs] = useState<Preferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token')
    return { 'Authorization': token ? `Bearer ${token}` : '' }
  }

  useEffect(() => {
    loadPrefs()
  }, [])

  const loadPrefs = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.notifications.preferences, {
        headers: getAuthHeaders(),
      })
      if (res.ok) setPrefs(await res.json())
    } catch (err) {
      console.error('Failed to load preferences:', err)
    } finally {
      setLoading(false)
    }
  }

  const savePrefs = async () => {
    if (!prefs) return
    setSaving(true)
    setSaved(false)
    try {
      const res = await fetch(API_ENDPOINTS.notifications.preferences, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          email_reminders_enabled: prefs.email_reminders_enabled,
          reminder_frequency: prefs.reminder_frequency,
          survey_reminders: prefs.survey_reminders,
          organization_updates: prefs.organization_updates,
          quiet_hours_enabled: prefs.quiet_hours_enabled,
          quiet_hours_start: prefs.quiet_hours_start,
          quiet_hours_end: prefs.quiet_hours_end,
          timezone: prefs.timezone,
          muted_survey_ids: prefs.muted_survey_ids,
        }),
      })
      if (res.ok) {
        setPrefs(await res.json())
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    } catch (err) {
      console.error('Failed to save preferences:', err)
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof Preferences, value: any) => {
    if (!prefs) return
    setPrefs({ ...prefs, [field]: value })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      </div>
    )
  }

  if (!prefs) {
    return (
      <PageLayout>
        <PageHeader
          title={intl.formatMessage({ id: 'notif.pageTitle' })}
          backTo="/dashboard"
          backLabel={intl.formatMessage({ id: 'notif.backToDashboard' })}
        />
        <Card elevated>
          <p className="text-foreground-muted">
            <FormattedMessage id="notif.failedToLoad" />
          </p>
        </Card>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title={intl.formatMessage({ id: 'notif.pageTitle' })}
        subtitle={intl.formatMessage({ id: 'notif.subtitle' })}
        backTo="/dashboard"
        backLabel={intl.formatMessage({ id: 'notif.backToDashboard' })}
        actions={
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-success text-sm font-medium">
                <FormattedMessage id="notif.saved" />
              </span>
            )}
            <Button variant="primary" onClick={savePrefs} disabled={saving}>
              {saving ? (
                <FormattedMessage id="notif.saving" />
              ) : (
                <FormattedMessage id="notif.saveChanges" />
              )}
            </Button>
          </div>
        }
      />

      {/* Global Email Toggle */}
      <Card elevated className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          <FormattedMessage id="notif.emailNotifications" />
        </h3>
        <div className="space-y-4">
          <ToggleRow
            label={intl.formatMessage({ id: 'notif.emailRemindersLabel' })}
            description={intl.formatMessage({ id: 'notif.emailRemindersDesc' })}
            checked={prefs.email_reminders_enabled}
            onChange={(v) => updateField('email_reminders_enabled', v)}
          />
          {prefs.email_reminders_enabled && (
            <div className="ml-8 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-muted mb-1">
                  <FormattedMessage id="notif.emailFrequency" />
                </label>
                <select
                  className="border border-border rounded-lg px-3 py-2 text-sm w-64"
                  value={prefs.reminder_frequency}
                  onChange={(e) => updateField('reminder_frequency', e.target.value)}
                >
                  {FREQUENCY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {intl.formatMessage({ id: opt.labelId })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Notification Types */}
      <Card elevated className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          <FormattedMessage id="notif.notificationTypes" />
        </h3>
        <div className="space-y-4">
          <ToggleRow
            label={intl.formatMessage({ id: 'notif.surveyRemindersLabel' })}
            description={intl.formatMessage({ id: 'notif.surveyRemindersDesc' })}
            checked={prefs.survey_reminders}
            onChange={(v) => updateField('survey_reminders', v)}
          />
          <ToggleRow
            label={intl.formatMessage({ id: 'notif.orgUpdatesLabel' })}
            description={intl.formatMessage({ id: 'notif.orgUpdatesDesc' })}
            checked={prefs.organization_updates}
            onChange={(v) => updateField('organization_updates', v)}
          />
        </div>
      </Card>

      {/* Quiet Hours */}
      <Card elevated className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          <FormattedMessage id="notif.quietHours" />
        </h3>
        <div className="space-y-4">
          <ToggleRow
            label={intl.formatMessage({ id: 'notif.enableQuietHoursLabel' })}
            description={intl.formatMessage({ id: 'notif.enableQuietHoursDesc' })}
            checked={prefs.quiet_hours_enabled}
            onChange={(v) => updateField('quiet_hours_enabled', v)}
          />
          {prefs.quiet_hours_enabled && (
            <div className="ml-8 flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-muted mb-1">
                  <FormattedMessage id="notif.from" />
                </label>
                <input
                  type="time"
                  className="border border-border rounded-lg px-3 py-2 text-sm"
                  value={prefs.quiet_hours_start || '22:00'}
                  onChange={(e) => updateField('quiet_hours_start', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-muted mb-1">
                  <FormattedMessage id="notif.to" />
                </label>
                <input
                  type="time"
                  className="border border-border rounded-lg px-3 py-2 text-sm"
                  value={prefs.quiet_hours_end || '08:00'}
                  onChange={(e) => updateField('quiet_hours_end', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Timezone */}
      <Card elevated className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          <FormattedMessage id="notif.timezone" />
        </h3>
        <select
          className="border border-border rounded-lg px-3 py-2 text-sm w-64"
          value={prefs.timezone}
          onChange={(e) => updateField('timezone', e.target.value)}
        >
          {TIMEZONE_OPTIONS.map(tz => (
            <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </Card>

      {/* Muted Surveys */}
      {prefs.muted_survey_ids.length > 0 && (
        <Card elevated className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            <FormattedMessage id="notif.mutedSurveys" />
          </h3>
          <p className="text-sm text-foreground-muted mb-3">
            <FormattedMessage id="notif.mutedSurveysDesc" />
          </p>
          <div className="space-y-2">
            {prefs.muted_survey_ids.map(id => (
              <div key={id} className="flex items-center justify-between bg-background rounded-lg px-3 py-2">
                <span className="text-sm text-foreground">{id}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await fetch(API_ENDPOINTS.notifications.unmuteSurvey(id), {
                      method: 'POST',
                      headers: getAuthHeaders(),
                    })
                    updateField('muted_survey_ids', prefs.muted_survey_ids.filter(s => s !== id))
                  }}
                >
                  <FormattedMessage id="notif.unmute" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </PageLayout>
  )
}

// Toggle switch component
const ToggleRow: React.FC<{
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}> = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between">
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-foreground-muted">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-primary' : 'bg-border'
      }`}
      onClick={() => onChange(!checked)}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-surface transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
)

export default NotificationSettingsPage
